import { isMac, isTauri } from '@heidi/runtime/platform';

/** Platform bucket used to pick the correct hotkey settings field + defaults. */
export type DictationHotkeyPlatform = 'web' | 'mac' | 'windows';

export const getUserPlatform = (): DictationHotkeyPlatform => {
  switch (true) {
    case !isTauri():
      return 'web';
    case isMac:
      return 'mac';
    default:
      return 'windows';
  }
};

/** Settings field that stores the hotkey chord for each platform. */
export const PLATFORM_HOTKEYS_FIELD = {
  web: 'hotkeys',
  mac: 'mac_hotkeys',
  windows: 'windows_hotkeys',
} as const satisfies Record<DictationHotkeyPlatform, string>;

/** Modifier set + optional terminal key. Used inside the recorder/form. */
export type HotkeyCombo<Mod extends string = string, Key extends string = string> = {
  modifiers: Mod[];
  key: Key | null;
};

/**
 * Canonical modifier order using W3C `KeyboardEvent.code` names.
 *
 * The slot ordering matches the HOTKEY design doc:
 *   Fn / Meta-on-Windows → Control → Alt → Shift → Meta-on-mac
 *
 * In W3C, `MetaLeft`/`MetaRight` is Cmd on mac and Win on windows — same
 * physical key position, same name. Slot priority is the same on both
 * platforms since modifiers serialise in increasing-slot order.
 *
 * Generic root names (`Control`, `Shift`, …) are kept for the reserved-combo
 * denylist normalisation that strips sides before comparison; they're not
 * part of the recordable vocabulary.
 *
 * Used by `encodeChord` so the same chord always serializes the same way.
 */
export const MOD_ORDER: Readonly<Record<string, number>> = {
  Fn: 0,
  Control: 1,
  ControlLeft: 1,
  ControlRight: 1,
  Alt: 2,
  AltLeft: 2,
  AltRight: 2,
  Shift: 3,
  ShiftLeft: 3,
  ShiftRight: 3,
  Meta: 4,
  MetaLeft: 4,
  MetaRight: 4,
};

/**
 * Encode a `{ modifiers, key }` combo to the wire-format array. Modifier
 * order is normalised so two chords with the same logical content serialize
 * identically.
 */
export const encodeChord = (combo: HotkeyCombo): string[] => {
  const ordered = [...combo.modifiers].sort(
    (a: string, b: string) => (MOD_ORDER[a] ?? 99) - (MOD_ORDER[b] ?? 99)
  );
  return combo.key === null ? ordered : [...ordered, combo.key];
};
